<!--
  jt-form-field.component.html
  ============================
  Two rendering modes:
    MODE 1 — hasBuiltInControl = true  → renders input/textarea/password internally
    MODE 2 — hasBuiltInControl = false → projects custom control via <ng-content>

  NOTE: Outer layout classes (container-[name]), host test-ids (container-[name]), 
  and 'display: contents' (for inline mode) are applied via HostBinding in the 
  TypeScript controller to ensure strict Bootstrap compatibility and consistent testing hooks.
-->

<label
  [id]="computedId + '-label'"
  [for]="hasBuiltInControl ? computedId : (projectedControlId || null)"
  [class]="'control-label col-md-' + labelCols">
  {{ label | translate }}
  @if (required) {
  <span class="text-danger" aria-hidden="true">&thinsp;*</span>
  }
</label>

<div [class]="'col-md-' + fieldCols">

  @if (hasBuiltInControl) {

  @if (type === 'text' || type === 'email' || type === 'number') {
  <input [id]="computedId" [type]="type"
    [ngClass]="'form-control ' + computedInternalClass"
    [class.is-invalid]="showErrors"
    [value]="value" [attr.data-testid]="computedTestId"
    [placeholder]="placeholder | translate" [disabled]="disabled" [readonly]="readonly"
    [attr.minlength]="minLength" [attr.maxlength]="maxLength"
    [attr.aria-required]="required ? 'true' : null" [attr.aria-invalid]="showErrors ? 'true' : null"
    [attr.aria-describedby]="showErrors ? errorId : null" (input)="onInputChange($event)" (blur)="onBlur()">
  }

  @if (type === 'textarea') {
  <textarea [id]="computedId" [rows]="rows"
    [ngClass]="'form-control ' + computedInternalClass"
    [class.is-invalid]="showErrors" [attr.data-testid]="computedTestId"
    [placeholder]="placeholder | translate" [disabled]="disabled" [readonly]="readonly"
    [attr.minlength]="minLength" [attr.maxlength]="maxLength"
    [attr.aria-required]="required ? 'true' : null" [attr.aria-invalid]="showErrors ? 'true' : null"
    [attr.aria-describedby]="showErrors ? errorId : null" (input)="onInputChange($event)"
    (blur)="onBlur()">{{ value }}</textarea>
  }

  @if (type === 'select') {
  <select [id]="computedId" [disabled]="disabled"
    [ngClass]="'form-control ' + computedInternalClass"
    [class.is-invalid]="showErrors" [attr.data-testid]="computedTestId"
    [attr.aria-required]="required ? 'true' : null" [attr.aria-invalid]="showErrors ? 'true' : null"
    [attr.aria-describedby]="showErrors ? errorId : null" [ngModel]="value" (ngModelChange)="onInputChange($event)" (blur)="onBlur()">
    @for (opt of selectOptions; track opt.value) {
      <option [value]="opt.value">{{ opt.label | translate }}</option>
    }
  </select>
  }

  @if (type === 'dropdown') {
  <app-ngx-select-dropdown 
    [id]="computedId"
    [testIdInput]="computedTestId"
    [ngModel]="value" (ngModelChange)="onInputChange($event)"
    [config]="dropdownConfig" 
    [options]="dropdownOptions" 
    [multiple]="dropdownMultiple"
    [customClass]="computedInternalClass">
  </app-ngx-select-dropdown>
  }

  @if (type === 'datepicker') {
  <app-jt-datepicker 
    [id]="computedId"
    [ngModel]="value" (ngModelChange)="onInputChange($event)"
    [minDate]="datepickerMinDate" 
    [maxDate]="datepickerMaxDate"
    [serverFormat]="datepickerServerFormat"
    [class]="computedInternalClass"
    [testId]="computedTestId">
  </app-jt-datepicker>
  }

  @if (type === 'toggle') {
  <app-toggle-switch
    [isChecked]="value"
    [isDisabled]="disabled"
    [isTrueFalseValue]="toggleTrueFalseValue"
    [trueValue]="toggleTrueValue"
    [falseValue]="toggleFalseValue"
    (byChange)="onInputChange($event)">
  </app-toggle-switch>
  }

  @if (type === 'file') {
  <input [id]="computedId" type="file"
    [ngClass]="'form-control ' + computedInternalClass"
    [class.is-invalid]="showErrors"
    [attr.accept]="accept || null"
    [attr.multiple]="multiple ? '' : null"
    [attr.data-testid]="computedTestId"
    [disabled]="disabled"
    [attr.aria-required]="required ? 'true' : null"
    [attr.aria-invalid]="showErrors ? 'true' : null"
    [attr.aria-describedby]="showErrors ? errorId : null"
    (change)="onFileChange($event)">
  }

  }

  @if (!hasBuiltInControl) {
  <div
    [id]="computedId + '-container'"
    [attr.aria-labelledby]="projectedControlId ? null : (computedId + '-label')"
    [attr.aria-describedby]="projectedControlId ? null : (showErrors ? errorId : null)">
    <ng-content></ng-content>
  </div>
  }

  <!-- Validation errors (Applies to Mode 1 and Mode 2) -->
  @if (showErrors && errors) {
  <div [id]="errorId" class="invalid-feedback d-block" role="alert" aria-live="polite"
    aria-atomic="true">
    @if (errors['required'] && requiredError) {
    <span>{{ requiredError | translate }}</span>
    }
    @if (errors['email'] && emailError) {
    <span>{{ emailError | translate }}</span>
    }
    @if (errors['minlength'] && minLengthError) {
    <span>{{ minLengthError | translate }}</span>
    }
    @if (errors['maxlength'] && maxLengthError) {
    <span>{{ maxLengthError | translate }}</span>
    }
    @if (errors['pattern'] && patternError) {
    <span>{{ patternError | translate }}</span>
    }
    @if (customError &&
    !errors['required'] &&
    !errors['email'] &&
    !errors['minlength'] &&
    !errors['maxlength'] &&
    !errors['pattern']) {
    <span>{{ customError | translate }}</span>
    }
  </div>
  }

</div>
